import React from "react"
import Image from "next/image"
import classNames from "classnames"
import { GetStaticProps, GetStaticPaths, InferGetStaticPropsType } from "next"
import characteristics from "../../../constants/breed-characteristics/breed-characteristics-content.json"
import cost from "../../../constants/breed-characteristics/breed-costs.json"
import moreOnBreed from "../../../constants/breed-characteristics/more-on-breed.json"
import { urlifyComplexName } from "../../../constants/stateCityDictionary"
import PageHead from "../../../components/head/PageHead"
import { PremiumHeroForm } from "../../../components/premium/hero-form"
import PageNav from "../../../storyblok/sections/PageNav"
import heroImages from "../../../constants/pet-breed/breeds-images.json"
import { calculateAverage } from "../../../util/formatNumber"
import QuoteBlock from "../../../storyblok/sections/GetQuotes"
import Footer from "../../../storyblok/static/Footer"
import TableOfContents from "../../../components/table-of-contents"
import RSSFeed from "../../../components/rss-feed"
import axios from "axios"
import Shadow from "../../../assets/images/blue-shadow.png"
import ShadowFooter from "../../../assets/images/blue-shadow-footer.png"
import Breadcrumb from "../../../components/breadcrumbs"
import { SWRConfig } from "swr"
import { unstable_serialize } from "swr/infinite"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
// Add cache keys for better organization
const CACHE_KEYS = {
  BREED_DATA: (breed: string) => `breed-${breed}`,
  RSS_FEED: (url: string) => `rss-${url}`,
  COSTS: (breed: string) => `costs-${breed}`,
}

// Add revalidation times
const REVALIDATE_TIMES = {
  BREED_PAGE: 86400, // 24 hours
  RSS_FEED: 3600, // 1 hour
}

export default function BreedPage({
  fallback,
  ...props
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <SWRConfig value={{ fallback }}>
      <BreedPageContent {...props} />
    </SWRConfig>
  )
}

// Separate the content component to work with SWR
function BreedPageContent({
  breedData,
  heroImageData,
  costs,
  moreOnBreedsData,
  averageTraining,
  averageFood,
  averageToysTreats,
}: Omit<InferGetStaticPropsType<typeof getStaticProps>, "fallback">) {
  const [isShown, setIsShown] = React.useState(false)

  if (!breedData) {
    return <div>Breed not found</div>
  }
  const detectVowelBeginning = ["A", "E", "I", "O", "U"].includes(
    breedData.breed.charAt(0).toUpperCase(),
  )
    ? "an"
    : "a"

  return (
    <div>
      <PageHead
        title={breedData.title}
        description={String(breedData.meta_description)}
      />
      <>
        <PageNav
          route={""}
          buttonLabel={""}
          navigationData={{ navItems: [], categories: [], isNavHidden: true }}
          position=""
        />

        <section
          id="premium-hero"
          className={classNames("relative md:h-[685px]")}
        >
          <div className="absolute h-[385px] inset-0 z-1 md:h-[78%]">
            <Image
              className="hidden md:block"
              src={heroImageData?.horizontal_image_url || ""}
              alt={breedData.breed}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
            />

            <Image
              className="md:hidden"
              src={heroImageData?.horizontal_image_url || ""}
              alt={breedData.breed}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
            />
          </div>

          <div className="absolute h-[385px] inset-0 bg-black bg-opacity-50 md:h-[78%] "></div>

          <div className="h-[385px] md:h-[78%] relative max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto px-6 lg:px-8 py-8 ">
            <div className="text-white font-merriweather max-w-3xl text-left">
              <p className="text-md font-light italic md:mt-10 lg:mt-20 mb-0">
                <Breadcrumb
                  items={[
                    { name: "Home", href: "/" },
                    { name: "All Cat Breeds", href: "/pets/cats/all-breeds" },
                    {
                      name: breedData.breed,
                      href: `/pets/cats/${urlifyComplexName(breedData.breed)}`,
                    },
                  ]}
                  linkClass="!text-white hover:!text-blue7"
                  className="!text-white not-italic mb-4"
                />
                CitizenShipper - Trusted by 125,000 pet owners since 2008!
              </p>
              <h1 className="text-[26px] leading-[50px] sm:text-[42px] sm:leading-[72px] md:text-[56px] font-bold lg:text-[56px] mt-1 md:mt-4 mb-6">
                Ultimate guide to {breedData.breed}
              </h1>
              <p
                id="PremiumHero-description"
                className="text-lg max-w-lg font-inter"
              >
                Learn everything there's to know about {breedData.breed}.
              </p>
            </div>
          </div>
          <div className="relative rounded lg:absolute lg:bottom-5 max-w-7xl mx-auto md:flex justify-center bg-white rounded-1 md:h-[200px] px-6 md:px-5 lg:px-10 py-11 right-0 left-0 hidden md:block">
            <PremiumHeroForm
              buttonLabel={"START YOUR JOURNEY"}
              buttonLink={"/shipments/new/animals/cats"}
            />
          </div>
        </section>
      </>

      <TableOfContents breed={breedData.breed} moreOnBreedsData={isShown} />
      <article className="w-full max-auto px-4 md:px-10 flex justify-center">
        <div className="max-w-[1200px] prose prose-headings:font-montserrat prose-h4:text-xl prose-h4:text-black prose-h4:font-semibold prose-p:text-base prose-h3:text-md prose-ul:ml-4 prose-ul:px-0 prose-li:relative prose-li:px-0 prose-li:mb-1">
          {safelyRenderHtml(breedData.breed_info)}
        </div>
      </article>
      <section
        id="personality"
        className="mx-auto md:py-5 md:mt-10 sm:bg-neutral-50"
      >
        <div className="p-4 md:px-8 md:pt-8 md:pb-0 max-w-7xl mx-auto w-full mb-14">
          <div className="flex flex-col md:flex-row justify-between items-center mx-auto">
            <div className="mt-10 md:mt-0 md:w-1/2 xl:w-2/3 md:pr-10 xl:pr-16">
              {
                <div className="prose-h3:text-2xl prose-h4:mb-3 prose-h3:md:leading-[54px] prose-h4:-mt-2">
                  {safelyRenderHtml(breedData.personality)}
                </div>
              }
            </div>
            <div className="md:w-1/2 xl:w-1/3 md:px-2 mb-5 md:mb-0 md:flex justify-center">
              <div className="relative ">
                <Image
                  className="relative z-20 w-[347px] h-[300px] object-cover object-top rounded-2xl rounded-br-full rounded-bl-full rounded-tl-full -right-2.5 top-4 md:-right-2 md:top-3"
                  src={heroImageData?.portrait_image_url || ""}
                  alt={heroImageData?.portrait_image_alt_text || "Dog"}
                  height={300}
                  width={347}
                />
                <Image
                  src={Shadow.src}
                  alt="Man with dog"
                  className="absolute top-1 z-0 object-cover w-[343px] h-[300px]"
                  height={300}
                  width={343}
                  loading="lazy"
                />
                <Image
                  src={ShadowFooter.src}
                  alt="Man with dog"
                  className="object-contain w-[108px] h-[90px] absolute -bottom-4 -right-4 md:right-0 xl:left-[245px] flex items-end justify-end z-50"
                  width={108}
                  height={90}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <article className="w-full max-auto px-4 md:px-10 flex justify-center mt-10">
        <div className="max-w-[1200px] prose prose-headings:font-montserrat prose-h4:text-xl prose-h4:text-black prose-h4:font-semibold prose-p:text-base prose-h3:text-md prose-ul:ml-4 prose-ul:px-0 prose-li:relative prose-li:px-0 prose-li:mb-1">
          {safelyRenderHtml(breedData.exercise)}
        </div>
      </article>
      <section className="max-w-[1200px] mx-auto flex">
        <article className="w-full max-auto md:px-10 my-10 md:grid grid-cols-3 md:gap-x-20 justify-center items-center">
          <div className="col-span-1 md:pr-10">
            <div className="relative flex items-center justify-center pt-8 md:p-10">
              <div className="relative">
                <div className="transform -rotate-2 bg-white p-4 shadow-lg  w-[299px] h-[530px] md:w-[436px] md:h-[612px] flex justify-center items-center">
                  <Image
                    src={heroImageData?.portrait_image_url || ""}
                    alt={breedData.name}
                    width={375}
                    height={510}
                    className="w-[280px] h-[430px] md:w-[375px] md:h-[510px] object-center"
                  />
                </div>
              </div>

              <div className="absolute top-24 right-[1rem] md:right-[-8rem] flex flex-col space-y-4 text-sm">
                <div className="bg-white rounded-full py-2 px-4 shadow-lg  text-blue-600">
                  ~{" $"}
                  {averageFood} <span className="text-black">Animal food</span>
                </div>
                <div className="bg-white rounded-full py-2 px-4 shadow-lg  text-blue-600">
                  ~{" $"}
                  {averageToysTreats}{" "}
                  <span className="text-black">Toys and treats</span>
                </div>
                <div className="bg-white rounded-full py-2 px-4 shadow-lg  text-blue-600">
                  ~{" $"}
                  {averageTraining} <span className="text-black">Training</span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="col-span-2 xl:pl-10 xl:ml-10 px-4 mt-4"
            id={`cost-of-owning-${urlifyComplexName(breedData.breed)}`}
          >
            <div className="prose-h3:leading-[44px] prose-h3:mb-4">
              {safelyRenderHtml(breedData.cost_of_owning_intro)}
            </div>
            <div>
              <h4 className="text-base mt-8 mb-4">
                Price of {breedData.breed} ownership - First 12 months *
              </h4>

              {/* Cost Breakdown List */}
              <ul className="space-y-4 text-gray-800 text-sm p-0">
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {" "}
                    {costs.purchase_low} - {costs.purchase_high}{" "}
                  </span>
                  <span>
                    Purchasing {detectVowelBeginning} {breedData.breed}
                  </span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_licensing_registration_low} -{" "}
                    {costs.first_year_licensing_registration_high}
                  </span>
                  <span>Licensing/registration</span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_pet_insurance_low} -{" "}
                    {costs.first_year_pet_insurance_high}{" "}
                  </span>
                  <span>Insurance</span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_medical_low} -{" "}
                    {costs.first_year_medical_high}
                  </span>
                  <span className="text-right flex flex-col sm:flex-row">
                    First-year medical expenses:{" "}
                    <span>vet visits, vaccinations</span>
                  </span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_food_low} - {costs.first_year_food_high}
                  </span>
                  <span className="text-right flex flex-col sm:flex-row">
                    Food (depends on size of dog/<span>brand of food)</span>
                  </span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_medical_low} -{" "}
                    {costs.first_year_medical_high}
                  </span>
                  <span className="text-sm">Training</span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_toys_treats_low} -{" "}
                    {costs.first_year_toys_treats_high}
                  </span>
                  <span>Toys and treats</span>
                </li>
                <li className="flex justify-between border-b border-gray-200 pb-0.5">
                  <span>
                    {costs.first_year_grooming_low} -{" "}
                    {costs.first_year_grooming_high}
                  </span>
                  <span>Grooming</span>
                </li>
              </ul>

              {/* Total Expense */}
              <div className="mt-6 text-base font-bold text-blue-600">
                {costs.first_year_total_cost_low} -{" "}
                {costs.first_year_total_cost_high}{" "}
                <span className="text-gray-800 font-normal">
                  Total expense for the first year
                </span>
              </div>

              {/* Note Section */}
              <div className="mt-4 text-sm text-gray-600 italic">
                <strong>Note:</strong> These estimates are compiled by our team
                of researchers based on market averages. Actual costs may vary
                depending on your location, individual pet needs,
                breeder/adoption fees, veterinary rates, and product choices.
                Please consult local veterinarians and pet care providers for
                costs specific to your situation.
              </div>
            </div>
          </div>
        </article>
      </section>
      <article className="w-full max-auto px-4 md:px-10 flex justify-center md:my-10">
        <div className="max-w-[1200px] prose prose-headings:font-montserrat prose-h4:text-xl prose-h4:text-black prose-h4:font-semibold prose-p:text-base prose-h3:text-md prose-ul:ml-4 prose-ul:px-0 prose-li:relative prose-li:px-0 prose-li:mb-1">
          {safelyRenderHtml(breedData.rescues)}
        </div>
      </article>

      <div
        className="max-w-[1200px] mx-auto px-4 mt-6 md:mt-16 md:pt-6 md:px-10 lg:px-0 mb-6"
        id="more-on-breed"
      >
        {isShown && (
          <h2 className="text-3xl text-blue7 text-center">
            More on {breedData.breed}s
          </h2>
        )}
        <RSSFeed url={moreOnBreedsData?.feed_url} setIsShown={setIsShown} />
      </div>

      <QuoteBlock
        blok={{
          heading: `Looking to ship your ${breedData.breed}?`,
          content:
            "No hidden costs or upcharges. Just a simple and stress-free experience. Get started in two minutes or less!",
          buttonLabel: "Get Free Estimates",
          buttonLink: "/shipments/new/animals/cats",
          bgColor: "bg-white",
        }}
      />
      <Footer />
    </div>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Filter and batch process paths
  const catBreeds = characteristics.filter((item) => item.animal === "cat")
  const paths = catBreeds.map((breed) => ({
    params: { breed: urlifyComplexName(breed.breed) },
  }))

  return {
    paths,
    fallback: "blocking", // Use blocking for better UX on cold starts
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    // Parallel data fetching for better performance
    const [
      breedData,
      heroImageData,
      costs,
      moreOnBreedsData,
    ] = await Promise.all([
      // Find breed data
      Promise.resolve(
        characteristics.find(
          (breed) => urlifyComplexName(breed.breed) === params?.breed,
        ),
      ),
      // Find hero image
      Promise.resolve(
        heroImages.find(
          (item) => urlifyComplexName(item.breed) === params?.breed,
        ) || null,
      ),
      // Find costs
      Promise.resolve(
        cost.find((item) => urlifyComplexName(item.breed) === params?.breed) ||
          null,
      ),
      // Find more on breed data
      Promise.resolve(
        moreOnBreed.find(
          (item) => urlifyComplexName(item.breed) === params?.breed,
        ) || null,
      ),
    ])

    if (!breedData) {
      return { notFound: true }
    }

    // Pre-calculate averages
    const averages = costs
      ? {
          training: calculateAverage(
            costs.first_year_training_low,
            costs.first_year_training_high,
          ),
          food: calculateAverage(
            costs.first_year_food_low,
            costs.first_year_food_high,
          ),
          toysTreats: calculateAverage(
            costs.first_year_toys_treats_low,
            costs.first_year_toys_treats_high,
          ),
        }
      : {
          training: null,
          food: null,
          toysTreats: null,
        }

    // Pre-fetch RSS feed if URL exists
    let rssFeedData = null
    if (moreOnBreedsData?.feed_url) {
      try {
        const response = await fetch(moreOnBreedsData.feed_url, {
          headers: {
            Accept: "application/rss+xml, application/xml, text/xml",
          },
          next: { revalidate: REVALIDATE_TIMES.RSS_FEED },
        })

        if (!response.ok) throw new Error("RSS fetch failed")
        rssFeedData = await response.text()
      } catch (error) {
        console.error(
          `Failed to pre-fetch RSS feed for ${breedData.breed}:`,
          error,
        )
      }
    }

    // Prepare SWR fallback data
    const fallback = {
      [CACHE_KEYS.BREED_DATA(params?.breed as string)]: breedData,
      [CACHE_KEYS.RSS_FEED(moreOnBreedsData?.feed_url || "")]: rssFeedData,
      [CACHE_KEYS.COSTS(params?.breed as string)]: costs,
    }

    return {
      props: {
        fallback,
        breedData,
        heroImageData,
        costs,
        moreOnBreedsData,
        averageTraining: averages.training,
        averageFood: averages.food,
        averageToysTreats: averages.toysTreats,
      },
      revalidate: REVALIDATE_TIMES.BREED_PAGE,
    }
  } catch (error) {
    console.error("Failed to fetch breed data", error)
    return { notFound: true }
  }
}
